import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as eAlertsApi from '../../api/eAlerts';
import { EAlertFormData } from '../../schemas/eAlert.schema';

/**
 * Hook for registering eAlerts for contacts
 * @returns Mutation function and state for registering eAlerts
 */
export const useRegisterEAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: EAlertFormData) => eAlertsApi.registerEAlert(data),
    onSuccess: (data, variables) => {
      // Invalidate and refetch contact data if needed
      if (variables.contact_id) {
        queryClient.invalidateQueries({
          queryKey: ['contact', variables.contact_id],
        });
        queryClient.invalidateQueries({
          queryKey: ['userProfile', variables.contact_id],
        });
      }
    },
    onError: (error) => {
      console.error('Error registering eAlert:', error);
    },
  });
};

/**
 * Hook for testing eAlerts without actually registering them
 * @returns Mutation function and state for testing eAlerts
 */
export const useTestEAlert = () => {
  return useMutation({
    mutationFn: (data: EAlertFormData) => eAlertsApi.testEAlert(data),
    onSuccess: (data) => {
      // Log test results for debugging
      console.log('eAlert test results:', data);
    },
    onError: (error) => {
      console.error('Error testing eAlert:', error);
    },
  });
};

/**
 * Hook for unsubscribing from eAlerts
 * @returns Mutation function and state for unsubscribing from eAlerts
 */
export const useUnsubscribeEAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contactId, organizationId }: { contactId: string; organizationId: string }) =>
      eAlertsApi.unsubscribeEAlert(contactId, organizationId),
    onSuccess: (data, { contactId }) => {
      // Invalidate and refetch contact data
      queryClient.invalidateQueries({
        queryKey: ['contact', contactId],
      });
      queryClient.invalidateQueries({
        queryKey: ['userProfile', contactId],
      });
    },
    onError: (error) => {
      console.error('Error unsubscribing from eAlert:', error);
    },
  });
};

/**
 * Hook for updating eAlert filters for a contact
 * @returns Mutation function and state for updating eAlert filters
 */
export const useUpdateEAlertFilters = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: EAlertFormData) => eAlertsApi.updateEAlertFilters(data),
    onSuccess: (data, variables) => {
      // Invalidate and refetch contact data if needed
      if (variables.contact_id) {
        queryClient.invalidateQueries({
          queryKey: ['contact', variables.contact_id],
        });
        queryClient.invalidateQueries({
          queryKey: ['userProfile', variables.contact_id],
        });
      }
    },
    onError: (error) => {
      console.error('Error updating eAlert filters:', error);
    },
  });
};
